import { useClearPasswordChangeObligation } from '@auth/entities/account';
import {
  changePasswordSchema,
  useChangePassword,
} from '@auth/features/profile-security';
import { useClerk, useUser } from '@clerk/clerk-react';
import { Lock } from 'lucide-react';
import { useState } from 'react';
import { trpc } from '@/shared/api/trpc';
import { Button, Heading, Input, InputGroup, Text } from '@/shared/ui';
import css from './force-password-change.module.scss';

type FieldErrors = Partial<
  Record<'currentPassword' | 'newPassword' | 'confirmPassword', string>
>;

/**
 * Full-screen gate shown while the signed-in user still holds an
 * admin-issued temporary password (XT-716).
 *
 * This is the UI half of a server-enforced rule: `protectedProcedure` refuses
 * every procedure but `auth.me` and the clear mutation until the obligation is
 * gone, so this screen is what the user can actually act on rather than the
 * thing that stops them. Rendering it in place of the app (instead of
 * redirecting) means no route can be reached around it.
 */
export const ForcePasswordChange = () => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const utils = trpc.useUtils();
  const { changePassword, isPending } = useChangePassword();
  const clearObligation = useClearPasswordChangeObligation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    const parsed = changePasswordSchema.safeParse({
      currentPassword,
      newPassword,
      confirmPassword,
    });

    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (
          key === 'currentPassword' ||
          key === 'newPassword' ||
          key === 'confirmPassword'
        ) {
          next[key] = issue.message;
        }
      }
      setErrors(next);
      return;
    }

    setErrors({});
    const ok = await changePassword({
      currentPassword: parsed.data.currentPassword,
      newPassword: parsed.data.newPassword,
    });
    if (!ok) return;

    // Clerk owns the password, so the server cannot see that the change
    // happened — tell it, then refetch `auth.me` so the guard re-evaluates
    // and the app renders. Order matters: clearing before invalidating means
    // the refetched `me` already carries the cleared flag.
    await clearObligation.mutateAsync();
    await utils.auth.me.invalidate();
  };

  return (
    <div className={css.root}>
      <form className={css.card} onSubmit={handleSubmit}>
        <Heading size="lg">Choose a new password</Heading>
        <Text size="sm" variant="muted">
          {user?.primaryEmailAddress?.emailAddress
            ? `You signed in to ${user.primaryEmailAddress.emailAddress} with a temporary password.`
            : 'You signed in with a temporary password.'}{' '}
          Set a password of your own to continue.
        </Text>

        <InputGroup
          error={errors.currentPassword}
          htmlFor="force-current-password"
          label="Temporary password"
        >
          <Input
            autoComplete="current-password"
            id="force-current-password"
            onChange={(e) => setCurrentPassword(e.target.value)}
            trailingIcon={<Lock size={16} />}
            type="password"
            value={currentPassword}
          />
        </InputGroup>

        <InputGroup
          error={errors.newPassword}
          htmlFor="force-new-password"
          label="New password"
        >
          <Input
            autoComplete="new-password"
            id="force-new-password"
            onChange={(e) => setNewPassword(e.target.value)}
            trailingIcon={<Lock size={16} />}
            type="password"
            value={newPassword}
          />
        </InputGroup>

        <InputGroup
          error={errors.confirmPassword}
          htmlFor="force-confirm-password"
          label="Confirm new password"
        >
          <Input
            autoComplete="new-password"
            id="force-confirm-password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            trailingIcon={<Lock size={16} />}
            type="password"
            value={confirmPassword}
          />
        </InputGroup>

        <div className={css.actions}>
          <Button
            disabled={isPending || clearObligation.isPending}
            size="lg"
            type="submit"
            variant="dark"
          >
            {isPending || clearObligation.isPending
              ? 'Saving...'
              : 'Set password and continue'}
          </Button>
          {/* The only way out other than setting a password. Without it a
              user who mistypes their temporary password has no exit. */}
          <Button
            onClick={() => void signOut({ redirectUrl: '/sign-in' })}
            type="button"
            variant="ghost"
          >
            Sign out
          </Button>
        </div>
      </form>
    </div>
  );
};
